import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useLogo } from '../../context/LogoContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';
import LanguageSelector from '../../components/LanguageSelector';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const { logoUrl } = useLogo();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role !== 'admin') {
        toast.error(t('admin_login_no_permission'));
        return;
      }
      toast.success(t('admin_login_success'));
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin_login_fail'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-4">
          <LanguageSelector />
        </div>
        <div className="bg-card border border-border/50 rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-accent" />
            </div>
            <div className="font-logo text-3xl font-bold text-primary mb-2 flex items-center justify-center gap-3">
              <img src={logoUrl} alt="PRC Remit" className="h-12 w-12 rounded-full object-cover" />
              PRC Remit
            </div>
            <p className="text-sm text-muted-foreground">{t('admin_login_desc')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input id="email" type="email" placeholder="admin@prcremit.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <Input id="password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password-input" />
            </div>

            <Button type="submit" className="w-full rounded-full h-12" disabled={loading} data-testid="admin-login-button">
              {loading ? t('admin_login_loading') : t('login')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
